// src/components/CardPaymentBrick/index.js
import { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import styles from './styles.module.css';

/*
    Card Payment Brick — gera o card_token_id necessário pra criar
    uma assinatura (preapproval) no Mercado Pago.

    Esse componente NÃO processa pagamento nenhum sozinho. Ele só
    tokeniza o cartão e devolve esse token pro seu backend, que
    então usa esse token na criação do preapproval.

    Props:
      amount        (number)   valor do plano, só pra exibição/validação do Brick
      payerEmail    (string)   e-mail do usuário logado
      onTokenReady  (function) chamado com (cardTokenId) quando o cartão é tokenizado
      onError       (function) chamado com (mensagem) em caso de erro
*/
export default function CardPaymentBrick({ amount, payerEmail, onTokenReady, onError }) {
    const containerRef = useRef(null);
    const brickControllerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [scriptError, setScriptError] = useState('');

    useEffect(() => {
        let cancelled = false;

        async function initBrick() {
            try {
                // Carrega o SDK JS do Mercado Pago (uma vez só, via CDN)
                if (!window.MercadoPago) {
                    await loadMercadoPagoScript();
                }
                if (cancelled) return;

                const publicKey = process.env.REACT_APP_MP_PUBLIC_KEY;
                if (!publicKey) {
                    setScriptError('Public Key do Mercado Pago não configurada.');
                    setLoading(false);
                    return;
                }

                const mp = new window.MercadoPago(publicKey, { locale: 'pt-BR' });
                const bricksBuilder = mp.bricks();

                const controller = await bricksBuilder.create('cardPayment', 'cardPaymentBrick_container', {
                    initialization: {
                        amount: amount,
                        payer: { email: payerEmail },
                    },
                    customization: {
                        visual: {
                            style: { theme: 'dark' }, // combina com o design system indigo/dark
                        },
                        paymentMethods: {
                            maxInstallments: 1, // assinatura recorrente não usa parcelamento
                        },
                    },
                    callbacks: {
                        onReady: () => {
                            if (!cancelled) setLoading(false);
                        },
                        onSubmit: (cardFormData) => {
                            // Não processamos pagamento aqui — só extraímos o token
                            // e devolvemos pro componente pai, que chama nosso backend.
                            return new Promise((resolve) => {
                                const cardTokenId = cardFormData?.token;

                                if (!cardTokenId) {
                                    onError?.('Não foi possível gerar o token do cartão. Verifique os dados.');
                                    resolve();
                                    return;
                                }

                                onTokenReady?.(cardTokenId);
                                resolve();
                            });
                        },
                        onError: (error) => {
                            console.error('Erro no Card Payment Brick:', error);
                            onError?.('Erro ao processar dados do cartão. Verifique e tente novamente.');
                        },
                    },
                });

                brickControllerRef.current = controller;
            } catch (err) {
                console.error('Erro ao inicializar Card Payment Brick:', err);
                if (!cancelled) {
                    setScriptError('Erro ao carregar o formulário de pagamento.');
                    setLoading(false);
                }
            }
        }

        initBrick();

        return () => {
            cancelled = true;
            // Desmonta o brick ao sair da tela, evita duplicar instância
            if (brickControllerRef.current?.unmount) {
                brickControllerRef.current.unmount();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amount, payerEmail]);

    if (scriptError) {
        return (
            <div className={styles.errorBox}>
                <AlertCircle size={18} />
                <span>{scriptError}</span>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            {loading && (
                <div className={styles.loadingBox}>
                    <Loader2 size={20} className={styles.spinner} />
                    <span>Carregando formulário de pagamento...</span>
                </div>
            )}
            <div id="cardPaymentBrick_container" ref={containerRef} className={styles.brickContainer} />
        </div>
    );
}

/* Carrega o SDK JS do MP uma única vez */
function loadMercadoPagoScript() {
    return new Promise((resolve, reject) => {
        const existing = document.getElementById('mp-sdk-script');
        if (existing) {
            existing.addEventListener('load', resolve);
            return;
        }
        const script = document.createElement('script');
        script.id = 'mp-sdk-script';
        script.src = 'https://sdk.mercadopago.com/js/v2';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}
